import { useEffect, useState, useCallback } from 'react';

// Singleton observer instance
let resizeObserver: ResizeObserver | null = null;
let pageTopSpacersElement: HTMLElement | null = null;
const subscribers = new Set<(height: number) => void>();
let currentHeight = 0;

// Initialize the singleton observer
const initializeObserver = () => {
  if (resizeObserver) return;

  pageTopSpacersElement = document.getElementById('page-top-spacers');
  
  if (!pageTopSpacersElement) {
    console.warn('Element with id="page-top-spacers" not found');
    return;
  }

  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const newHeight = entry.contentRect.height;
      currentHeight = newHeight;
      
      // Notify all subscribers
      subscribers.forEach(callback => callback(newHeight));
    }
  });

  resizeObserver.observe(pageTopSpacersElement);
  
  // Set initial height
  currentHeight = pageTopSpacersElement.getBoundingClientRect().height;
};

// Cleanup the singleton observer
const cleanupObserver = () => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  pageTopSpacersElement = null;
  subscribers.clear();
  currentHeight = 0;
};

/**
 * Hook to monitor the height of the page-top-spacers element
 * Uses a singleton ResizeObserver to efficiently share the observation across multiple components
 * 
 * @returns {number} The current navbar height of the page-top-spacers element
 */
export const usePageSpacers = (): { navbarSpacerHeight: number } => {
  const [navbarSpacerHeight, setNavbarSpacerHeight] = useState<number>(currentHeight);

  const handleHeightChange = useCallback((newHeight: number) => {
    setNavbarSpacerHeight(newHeight);
  }, []);

  useEffect(() => {
    // Initialize observer if not already done
    initializeObserver();

    // Add this component as a subscriber
    subscribers.add(handleHeightChange);

    // Set initial height if available
    if (currentHeight > 0) {
      setNavbarSpacerHeight(currentHeight);
    }

    return () => {
      // Remove this component as a subscriber
      subscribers.delete(handleHeightChange);

      // If no more subscribers, cleanup the observer
      if (subscribers.size === 0) {
        cleanupObserver();
      }
    };
  }, [handleHeightChange]);

  return { navbarSpacerHeight };
};

export default usePageSpacers;
